import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { AxiosError } from "axios";
import { z } from "zod";

import {
    ControlOperationFailedError,
    PartialControlForUserListDto,
} from "api/generated/model";
import { ReassignmentFailure } from "org/user/components/ReassignmentFailureAlert";
import { STATUS_CODE_ERROR_OPERATION_FAILED } from "utils/errorCodes";

const controlOperationFailedErrorSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("SegregationOfDutiesViolated"),
        data: z.array(z.string()),
    }),
    z.object({
        type: z.literal("IllegalReviewerState"),
        data: z.array(z.string()),
    }),
]) satisfies z.ZodType<ControlOperationFailedError>;

export function describeControlFailure(
    t: TFunction,
    error: unknown,
    data: PartialControlForUserListDto
): ReassignmentFailure | null {
    if (
        !(error instanceof AxiosError) ||
        error.response?.status !== STATUS_CODE_ERROR_OPERATION_FAILED
    ) {
        return null;
    }
    const parsed = controlOperationFailedErrorSchema.safeParse(
        error.response.data
    );
    if (!parsed.success) {
        return null;
    }
    const body = parsed.data;
    const titleOf = (id: string) =>
        data.content.find((dto) => dto.resource.id === id)?.resource.title ??
        id;

    switch (body.type) {
        case "SegregationOfDutiesViolated":
            return {
                message: t(
                    "AccessAndAssignment_ReassignError_SegregationOfDuty_Controls"
                ),
                items: body.data.map(titleOf),
            };
        case "IllegalReviewerState":
            return {
                message: t("AccessAndAssignment_ReassignError_IllegalReviewer"),
                items: body.data.map(titleOf),
            };
        default:
            return assertNever(body);
    }
}
